import { Box, Stack, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { C1Chip } from 'c1ui/components/C1Chip'
import { FC, useId } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { C1LabeledTextField, C1LabeledTextFieldProps } from './C1LabeledTextField'

type C1LabeledTextFieldWithPresetsProps = {
    presets?: string[]
    setValue?: (value: string) => void
} & C1LabeledTextFieldProps

/**
 * Renders a labeled text field along with a set of preset chips. Clicking on a
 * chip sets the text field's value to the chip's corresponding preset value.
 *
 * See `frontend/components/pages/task/action/messages.ts` for helpers to get
 * presets for a given task action
 *
 * @example
 * <C1LabeledTextFieldWithPresets presets={['A', 'B', 'C']} setValue={setComment}} value={comment} />
 *
 * @example
 * // as a controlled input with react-hook-form <Controller />
 * <Controller
 *   control={control}
 *   render={({ field }) => <C1LabeledTextFieldWithPresets {...field} setValue={field.onChange} />}
 * />
 */

export const C1LabeledTextFieldWithPresets: FC<C1LabeledTextFieldWithPresetsProps> = ({ presets, setValue, ...props }) => {
    const presetsGroupId = useId()
    const presetsDescriptionId = useId()
    const intl = useIntl()
    const hasPresets = presets && setValue && presets.length > 0

    return <Box sx={{ mb: 2 }}>
        <C1LabeledTextField
            InputProps={{
                sx: hasPresets ? { flexDirection: 'column', alignItems: 'flex-start' } : undefined,
                endAdornment: hasPresets && (
                    <Box
                        role="group"
                        aria-labelledby={presetsGroupId}
                        aria-describedby={presetsDescriptionId}
                        sx={{ width: '100%' }}
                    >
                        <Typography
                            id={presetsGroupId}
                            sx={visuallyHidden}
                        >
                            <FormattedMessage id="component_labeled_text_field_with_presets_group_label" defaultMessage="Quick preset options" />
                        </Typography>
                        <Typography
                            id={presetsDescriptionId}
                            sx={visuallyHidden}
                        >
                            <FormattedMessage id="component_labeled_text_field_with_presets_description" defaultMessage="Click a preset to fill the field with that value" />
                        </Typography>
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            sx={props.multiline ? { mt: 2 } : { width: '100%', px: 2, pb: 1 }}
                        >
                            {presets.map((preset) => (
                                <C1Chip
                                    key={preset}
                                    label={preset}
                                    color="info"
                                    variant="outlined"
                                    onClick={(): void => setValue(preset)}
                                    onKeyDown={(e): void => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            setValue(preset)
                                        }
                                    }}
                                    aria-label={intl.formatMessage({
                                        id: 'component_labeled_text_field_with_presets_chip_aria_label',
                                        defaultMessage: 'Set value to {preset}',
                                    }, { preset })}
                                    sx={{
                                        mb: 1,
                                        mr: 1,
                                        cursor: 'pointer',
                                        '&:focus-visible': {
                                            outline: '2px solid',
                                            outlineColor: 'primary.main',
                                            outlineOffset: '2px',
                                        },
                                    }}
                                    tabIndex={0}
                                />
                            ))}
                        </Stack>
                    </Box>
                ),
            }}
            {...props}
            aria-describedby={hasPresets ? `${props['aria-describedby'] || ''} ${presetsDescriptionId}`.trim() : props['aria-describedby']}
        />
    </Box>
}
